/**
 * Sidebar Navigation Component
 * Controls layouts, routes, links, collapsible state, and mobile responsive menus.
 */

import { logoutUser, currentUserRole } from '../auth.js';
import { showToast } from '../utils.js';

/**
 * Render Sidebar HTML into container
 */
export function renderSidebar() {
  const container = document.getElementById('sidebar-container');
  if (!container) return;

  const isAdmin = currentUserRole === 'Admin';

  container.innerHTML = `
    <!-- Brand / Logo -->
    <!-- Brand / Logo -->
    <div class="sidebar-brand d-flex align-items-center">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="32" height="32" class="me-2 flex-shrink-0">
        <path d="M24 20 h22 v4 h-4 v36 c0 16 6 20 16 20 s16-4 16-20 v-36 h-4 v-4 h18 v4 h-4 v36 c0 22 -10 28 -24 28 s-24-6 -24-28 v-36 h-4 z" fill="#ffffff" />
        <path d="M10 32 L85 69" stroke="#00aeef" stroke-width="4.5" stroke-linecap="round" />
        <path d="M93 73 L86 62 L80 73 Z" fill="#00aeef" />
        <g transform="translate(84, 10)">
          <circle cx="5" cy="5" r="5" fill="none" stroke="#ffffff" stroke-width="0.8" />
          <text x="5" y="8.2" font-family="Arial, sans-serif" font-size="5.5" font-weight="bold" text-anchor="middle" fill="#ffffff">TM</text>
        </g>
      </svg>
      <div class="d-flex flex-column">
        <span class="sidebar-brand-text font-heading text-white fw-bold lh-1">Urena Healthcare</span>
        <span class="text-white-50 fst-italic small mt-1" style="font-size: 0.68rem; letter-spacing: 0;">An Arena Of Healthy Life</span>
      </div>
    </div>

    <!-- Navigation Menu Items -->
    <ul class="sidebar-menu">
      <li class="sidebar-menu-item">
        <a href="#dashboard" class="sidebar-link" data-route="dashboard">
          <i class="bi bi-grid-1x2-fill"></i>
          <span class="sidebar-link-text">Dashboard</span>
        </a>
      </li>
      <li class="sidebar-menu-item">
        <a href="#doctors" class="sidebar-link" data-route="doctors">
          <i class="bi bi-people-fill"></i>
          <span class="sidebar-link-text">Doctors CRM</span>
        </a>
      </li>
      <li class="sidebar-menu-item">
        <a href="#sales" class="sidebar-link" data-route="sales">
          <i class="bi bi-cart-fill"></i>
          <span class="sidebar-link-text">Medicine Sales</span>
        </a>
      </li>
      <li class="sidebar-menu-item">
        <a href="#investment" class="sidebar-link" data-route="investment">
          <i class="bi bi-cash-stack"></i>
          <span class="sidebar-link-text">Investments</span>
        </a>
      </li>
      <li class="sidebar-menu-item">
        <a href="#reports" class="sidebar-link" data-route="reports">
          <i class="bi bi-bar-chart-line-fill"></i>
          <span class="sidebar-link-text">Reports & Analytics</span>
        </a>
      </li>
      <li class="sidebar-menu-item">
        <a href="#profile" class="sidebar-link" data-route="profile">
          <i class="bi bi-person-circle"></i>
          <span class="sidebar-link-text">Profile</span>
        </a>
      </li>
      <li class="sidebar-menu-item">
        <a href="#stockists" class="sidebar-link" data-route="stockists">
          <i class="bi bi-shop"></i>
          <span class="sidebar-link-text">Stockists</span>
        </a>
      </li>
      <li class="sidebar-menu-item">
        <a href="#chemists" class="sidebar-link" data-route="chemists">
          <i class="bi bi-capsule"></i>
          <span class="sidebar-link-text">Chemists</span>
        </a>
      </li>
      <li class="sidebar-menu-item">
        <a href="#dcr" class="sidebar-link" data-route="dcr">
          <i class="bi bi-journal-check"></i>
          <span class="sidebar-link-text">Daily Call Report (DCR)</span>
        </a>
      </li>
      <li class="sidebar-menu-item">
        <a href="#mtp" class="sidebar-link" data-route="mtp">
          <i class="bi bi-calendar-range-fill"></i>
          <span class="sidebar-link-text">Monthly Tour Plan (MTP)</span>
        </a>
      </li>
      ${isAdmin ? `
      <li class="sidebar-menu-item">
        <a href="#approvals" class="sidebar-link" data-route="approvals">
          <i class="bi bi-shield-check"></i>
          <span class="sidebar-link-text">Admin Approvals</span>
        </a>
      </li>
      ` : ''}
      ${isAdmin ? `
      <li class="sidebar-menu-item">
        <a href="#settings" class="sidebar-link" data-route="settings">
          <i class="bi bi-gear-fill"></i>
          <span class="sidebar-link-text">System Settings</span>
        </a>
      </li>
      ` : ''}
    </ul>

    <!-- Sidebar Footer with Logout action -->
    <div class="sidebar-footer">
      <button id="sidebar-logout-btn" class="btn btn-link sidebar-link w-100 border-0 text-start p-2 m-0 bg-transparent">
        <i class="bi bi-box-arrow-right text-danger"></i>
        <span class="sidebar-link-text text-danger">Log Out</span>
      </button>
    </div>
  `;
}

/**
 * Initialize event listeners for the Sidebar
 */
export function initSidebar() {
  const logoutBtn = document.getElementById('sidebar-logout-btn');
  if (logoutBtn) {
    logoutBtn.addEventListener('click', async () => {
      try {
        await logoutUser();
        showToast("Logged Out", "You have successfully signed out.", "info");
      } catch (error) {
        showToast("Error", "Could not complete logout.", "error");
      }
    });
  }

  // Create & mount Mobile Backdrop (for overlay clicks)
  let backdrop = document.querySelector('.sidebar-backdrop');
  if (!backdrop) {
    backdrop = document.createElement('div');
    backdrop.className = 'sidebar-backdrop d-none';
    document.body.appendChild(backdrop);
  }

  // Dismiss sidebar when clicking the overlay backdrop on mobile
  backdrop.addEventListener('click', () => {
    document.body.classList.remove('sidebar-open');
    backdrop.classList.add('d-none');
  });
}
